import { useEffect, useState } from 'react'
import { CurieSelect } from '~/components/common/CurieSelect'
import { useEDIFactCodes } from '~/hooks/useEDIFactCodes'
import { EdiFactSchemaType } from '~/protos/agents'
import { SchemaTypeProps } from './types'

export interface EdifactSchemaData {
  ediFactVersion: string
  ediFactMessage: string
}

export function EdiFact({ sourceDataType, samples, onChange, onSchemaChange }: SchemaTypeProps) {
  const { data: ediFactCodes } = useEDIFactCodes()
  const [ediFactVersion, setEdiFactVersion] = useState<string>('')
  const [ediFactMessage, setEdiFactMessage] = useState<string>('')

  useEffect(() => {
    if (sourceDataType === 'DATA_TYPE_EDIFACT') {
      onSchemaChange({
        ediFactVersion,
        ediFactMessage
      })
    }
  }, [ediFactVersion, ediFactMessage, sourceDataType, onSchemaChange])

  // Create arrays of EDI FACT version and message options for select dropdowns
  const ediFactVersionOptions = ediFactCodes
    ? Object.keys(ediFactCodes).map(version => ({
        label: version,
        key: version
      }))
    : []

  const ediFactMessageOptions =
    ediFactCodes && ediFactVersion
      ? (ediFactCodes[ediFactVersion] || []).map(message => ({
          label: message.replace(/.esl$/, ''),
          key: message.replace(/.esl$/, '')
        }))
      : []

  if (sourceDataType !== 'DATA_TYPE_EDIFACT') {
    return null
  }

  return (
    <div className='edifact-selection-container mt-4 grid grid-cols-2 gap-4'>
      <div>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] select-none'>
          Version
        </div>
        <CurieSelect
          items={ediFactVersionOptions}
          placeholder='Select EDIFACT version'
          selectedKeys={[ediFactVersion]}
          onSelectionChange={selectedKeys => {
            if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
              return
            }
            const version = selectedKeys.values().next().value as string
            setEdiFactVersion(version)
            // Reset message type when version changes
            if (
              ediFactCodes &&
              ediFactCodes[version] &&
              Object.keys(ediFactCodes[version]).length > 0
            ) {
              setEdiFactMessage(ediFactCodes[version][0]?.replace(/.esl$/, ''))
            }
          }}
        />
      </div>
      <div>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] select-none'>
          Message
        </div>
        <CurieSelect
          items={ediFactMessageOptions}
          placeholder='Select EDIFACT message'
          selectedKeys={[ediFactMessage]}
          onSelectionChange={selectedKeys => {
            if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
              return
            }
            const message = selectedKeys.values().next().value as string
            setEdiFactMessage(message)
          }}
        />
      </div>
    </div>
  )
}

export function createEdifactSchema(schemaData: EdifactSchemaData) {
  return EdiFactSchemaType.fromJSON({
    version: schemaData.ediFactVersion,
    message: schemaData.ediFactMessage
  })
}
